import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { blogPosts } from '../data/posts';
import BlogCard from '../components/BlogCard';
import { IoSearch, IoFilter } from 'react-icons/io5';

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredPosts, setFilteredPosts] = useState(blogPosts);

  // Get unique categories
  const categories = ['all', ...new Set(blogPosts.map(post => post.category))];

  // Update filtered posts when search term or category changes
  useEffect(() => {
    let filtered = blogPosts;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredPosts(filtered);
  }, [searchTerm, selectedCategory]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={containerVariants}
      className="max-w-6xl"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="mb-12">
        <h2 className="text-2xl lg:text-3xl font-bold text-white-1 mb-6">
          My <span className="gradient-text">Blog</span>
        </h2>
        <div className="w-20 h-1 bg-gradient-to-r from-accent-yellow to-orange-yellow-crayola rounded-full mb-8"></div>
        <p className="text-light-gray text-sm max-w-2xl">
          Sharing insights, tutorials, and experiences from my journey in web development, 
          DevOps, and the ever-evolving world of technology.
        </p>
      </motion.div>

      {/* Search and Filter */}
      <motion.div variants={itemVariants} className="mb-12">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
          {/* Search Bar */}
          <div className="relative flex-1 max-w-md">
            <IoSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-light-gray" size={20} />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-card-dark/50 border border-jet/30 rounded-lg text-white-1 placeholder-light-gray focus:outline-none focus:border-accent-yellow/50 focus:ring-2 focus:ring-accent-yellow/20 transition-all duration-300"
            />
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2">
            <IoFilter className="text-accent-yellow" size={20} />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-3 bg-card-dark/50 border border-jet/30 rounded-lg text-white-1 focus:outline-none focus:border-accent-yellow/50 focus:ring-2 focus:ring-accent-yellow/20 transition-all duration-300"
            >
              {categories.map(category => (
                <option key={category} value={category} className="bg-card-dark">
                  {category === 'all' ? 'All Categories' : category}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Results Count */}
        <div className="mt-4">
          <p className="text-light-gray text-sm">
            {filteredPosts.length} {filteredPosts.length === 1 ? 'article' : 'articles'} found
            {searchTerm && ` for "${searchTerm}"`}
            {selectedCategory !== 'all' && ` in ${selectedCategory}`}
          </p>
        </div>
      </motion.div>

      {/* Blog Posts Grid */}
      <motion.div variants={itemVariants}>
        {filteredPosts.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => (
              <BlogCard
                key={post.id}
                post={post}
                index={index}
              />
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
          >
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-lg font-semibold text-white-1 mb-2">
              No articles found
            </h3>
            <p className="text-light-gray text-sm mb-6">
              Try adjusting your search terms or category filter.
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('all');
              }}
              className="px-6 py-3 bg-accent-yellow text-bg-dark font-semibold rounded-lg hover:bg-orange-yellow-crayola transition-colors duration-300"
            >
              Clear Filters
            </button>
          </motion.div>
        )}
      </motion.div>

      {/* Newsletter Signup */}
      <motion.div 
        variants={itemVariants}
        className="mt-16 bg-gradient-to-r from-card-dark/50 to-jet/30 border border-accent-yellow/20 rounded-xl p-8 backdrop-blur-sm text-center"
      >
        <h3 className="text-lg font-bold text-white-1 mb-4">
          Stay Updated
        </h3>
        <p className="text-light-gray text-sm mb-6 max-w-2xl mx-auto">
          Get notified when I publish new articles about web development, DevOps, and technology insights.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
          <input
            type="email"
            placeholder="Enter your email"
            className="flex-1 px-4 py-3 bg-card-dark/50 border border-jet/30 rounded-lg text-white-1 placeholder-light-gray focus:outline-none focus:border-accent-yellow/50 focus:ring-2 focus:ring-accent-yellow/20 transition-all duration-300"
          />
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-6 py-3 bg-gradient-to-r from-accent-yellow to-orange-yellow-crayola text-bg-dark font-semibold rounded-lg hover:shadow-lg hover:shadow-accent-yellow/25 transition-all duration-300"
          >
            Subscribe
          </motion.button>
        </div>
        <p className="text-xs text-light-gray mt-4">
          No spam, unsubscribe at any time.
        </p>
      </motion.div>
    </motion.div>
  );
};

export default Blog;
